import { Box, List } from '@mui/material';
import {
  LoadingIndicator,
  CmsEmptyList,
  Routes,
  useIntersectionObserver,
} from '@xolv-mybrightlink/common';
import { useFetchSavedArticles } from '../hooks/useFetchSavedArticles';
import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { ArticleItem } from './ArticleItem';
import { Article } from '../models/ArticleResult';
import { removeBookmarkArticle } from '../hooks/useBookmarkArticle';

export const ArticleSavedList = () => {
  const [articles, setArticles] = useState<Article[] | null>(null);
  const [hasLoadMore, setHasLoadMore] = useState<boolean>();
  const [anchorGuid, setAnchorGuid] = useState<string | null>(null);
  const [isRemovingBookmark, setIsRemovingBookmark] = useState(false);
  const navigate = useNavigate();
  const lastItemRef = useRef<HTMLDivElement>(null);
  const savedArticleFetchResult = useFetchSavedArticles(anchorGuid, 10);

  const onLoadMore = useCallback(() => {
    if (hasLoadMore && savedArticleFetchResult.type === 'DATA') {
      setAnchorGuid(savedArticleFetchResult.anchorGuid);
    }
  }, [hasLoadMore, savedArticleFetchResult]);

  useIntersectionObserver(lastItemRef, onLoadMore);

  useEffect(() => {
    if (savedArticleFetchResult.type === 'DATA') {
      setArticles((currentArticles) => [
        ...(currentArticles ?? []),
        ...savedArticleFetchResult.articles,
      ]);
      setHasLoadMore(savedArticleFetchResult.hasLoadMore);
    }
  }, [savedArticleFetchResult]);

  const openArticleDetails = useCallback(
    (article: Article) => {
      navigate(Routes.resources.articles.slug(article.slug, 'all-articles'));
    },
    [navigate],
  );

  const viewAllArticles = useCallback(() => {
    navigate(Routes.resources.articles.index);
  }, [navigate]);

  const onBookmarkClicked = async (
    e: MouseEvent<SVGSVGElement>,
    article: Article,
    savedArticles: Article[],
    isRemoving: boolean,
  ) => {
    e.stopPropagation();
    if (!isRemoving) {
      setArticles(
        savedArticles.filter((item) => item.databaseId !== article.databaseId),
      );
      setIsRemovingBookmark(true);
      await removeBookmarkArticle(article.slug);
      setIsRemovingBookmark(false);
    }
  };

  return (
    <Box
      className='hide-scrollbar'
      sx={{
        height: '100%',
        overflowY: 'auto',
        justifyContent: 'center',
      }}
    >
      <List
        sx={{
          pl: 7.5,
          pr: 7.5,
        }}
      >
        {articles?.map((article, index) => (
          <ArticleItem
            key={article.id}
            article={article}
            ref={index === articles.length - 1 ? lastItemRef : null}
            onArticleClicked={() => openArticleDetails(article)}
            onBookmarkClicked={(event) =>
              onBookmarkClicked(event, article, articles, isRemovingBookmark)
            }
          />
        ))}
      </List>
      {savedArticleFetchResult.type === 'LOADING' && (
        <LoadingIndicator
          sx={{
            height: !hasLoadMore ? '100%' : undefined,
            py: 10,
          }}
        />
      )}
      {articles &&
        articles.length === 0 &&
        savedArticleFetchResult.type !== 'LOADING' && (
          <CmsEmptyList
            title='saved.article.empty'
            description='saved.article.empty-desc'
            action='saved.article.empty-view'
            onActionClicked={viewAllArticles}
          />
        )}
      {isRemovingBookmark && (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            top: 0,
            background: 'transparent',
            position: 'absolute',
          }}
        />
      )}
    </Box>
  );
};
